import { Box, Button, Snackbar, Alert } from "@mui/material";
import React, {
  useLayoutEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import { Formik } from "formik";
import validationSchema from "./Schema";
import {
  apiDeleteRequest,
  apiGetRequest,
  apiPostRequest,
  apiPutRequest,
} from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import muiStyles from "./styles/muiStyles";
import ContactDetails from "./components/ContactDetails";
import BusinessProfile from "./components/BusinessProfile";
import MakeBusinessShine, {
  BUSINESS_IMAGE_FIELDS,
} from "./components/MakeBusinessShine";
import ServiceSettings from "./components/ServiceSettings";
import OpeningHoursWorkingDays from "./components/OpeningHoursWorkingDays";
import StoreSettings from "./components/StoreSettings";
import RestaurantSettings from "./components/RestaurantSettings";
import BankAccount from "./components/BankAccount";
import { AppContext } from "../../Context/AppContext";
import { useTranslation } from "react-i18next";
import { useRevalidateOnLanguageChange } from "../../hooks/useRevalidateOnLanguageChange";
import emitter from "../../Utils/events/eventBus";

// Restaurants were hidden per COMY1-279 review feedback (Roei) when they were
// dropped from the product. They are back: the restaurant vertical is now
// implemented end to end (mirrored tables, /api/restaurants endpoints, members
// marketplace tab), so the section is rendered and written on save again.
// Kept as a flag rather than inlined so the decision stays easy to reverse.
const SHOW_RESTAURANT_SETTINGS = true;

const EMPTY_OPENING_HOURS = Array.from({ length: 7 }, (_, i) => ({
  dayOfWeek: i + 1,
  enabled: false,
  openTime: null,
  closeTime: null,
}));

// time_units ids: 1=hours, 2=days, 3=weeks, 4=minutes.
const TIME_UNIT_DAYS = 2;
const TIME_UNIT_MINUTES = 4;

const emptyFulfillment = (deliveryTimeUnit) => ({
  pickup: {
    enabled: false,
    address: null,
    minPickupTime: null,
    minPickupTimeUnit: TIME_UNIT_DAYS,
    useStoreSchedule: true,
    customSchedule: [],
  },
  delivery: {
    estimatedDeliveryTime: null,
    estimatedDeliveryTimeUnit: deliveryTimeUnit,
    areas: [],
  },
});

// A store ships in days; a restaurant delivers in minutes. Built per call so the
// two verticals never share one object.
const EMPTY_FULFILLMENT = emptyFulfillment(TIME_UNIT_DAYS);
const EMPTY_RESTAURANT_FULFILLMENT = emptyFulfillment(TIME_UNIT_MINUTES);

const INITIAL_VALUES = {
  businessLegalName: "",
  businessLocation: "",
  businessType: "",
  businessNumber: "",
  contactFullName: "",
  contactPhonePrefix: "+972",
  contactPhoneNumber: "",
  contactEmail: "",
  description: "",
  afterHoursPrice: "",
  afterHoursCurrency: "",
  afterHoursPriceEnabled: false,
  languages: [],
  bankAccount: { accountNumber: "" },
  servicesImageUrl: null,
  storeImageUrl: null,
  restaurantImageUrl: null,
  _servicesImageInitialUrl: null,
  _storeImageInitialUrl: null,
  _restaurantImageInitialUrl: null,
  _servicesImageTempFile: null,
  _storeImageTempFile: null,
  _restaurantImageTempFile: null,
  _servicesImageDeleted: false,
  _storeImageDeleted: false,
  _restaurantImageDeleted: false,
  openingHours: EMPTY_OPENING_HOURS,
  storeSettings: EMPTY_FULFILLMENT,
  restaurantSettings: EMPTY_RESTAURANT_FULFILLMENT,
};

async function uploadImageOfType(file, type) {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiPostRequest(
    endpoints.updateSupplierBusinessImgByType(type),
    formData,
    { "Content-Type": "multipart/form-data" }
  );
  return res?.data?.image_url ?? res?.data?.imageUrl ?? null;
}

async function deleteImageOfType(type) {
  return apiDeleteRequest(endpoints.updateSupplierBusinessImgByType(type));
}

export default function BusinessSettingsContainer() {
  const { t } = useTranslation(
    "page-business-settings",
    "validation-error-messages"
  );
  const { languages } = useContext(AppContext);

  const { formikRef } = useRevalidateOnLanguageChange();

  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [zones, setZones] = useState([]);
  const [isloading, setIsLoading] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [bankErrors, setBankErrors] = useState({
    bankId: "",
    branchId: "",
    accountNumber: "",
  });

  const [initialValues, setInitialValues] = useState(INITIAL_VALUES);

  const fetchBankBranches = async (bankId) => {
    if (!bankId) {
      setBranches([]);
      return;
    }
    try {
      const response = await apiGetRequest(endpoints.getBankBranches(bankId));
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error("Error fetching bank branches:", error);
      setBranches([]);
    }
  };

  const getSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const [settingsResponse, banksResponse, zonesResponse] =
        await Promise.all([
          apiGetRequest(endpoints.getSupplierSettings),
          apiGetRequest(endpoints.getAllBanks),
          apiGetRequest(endpoints.getZones),
        ]);

      const settings = settingsResponse.data;

      // Per COMY1-279 AC #14: if the supplier has never set a pickup address,
      // default the "Pickup from" input to the read-only business address from
      // the same page. We push it into Formik state so it persists on save
      // unless the supplier picks a different address from Google Maps.
      const storeSettings = settings.storeSettings || EMPTY_FULFILLMENT;
      // A pickup address counts as "set" if any of its parts exist — a
      // city-only pick (e.g. "Jerusalem") leaves `street` empty but still has
      // a city/placeId, so we must not clobber it with the business address.
      const pickupAddr = storeSettings?.pickup?.address;
      const hasPickupAddress = !!(
        pickupAddr?.street ||
        pickupAddr?.city ||
        pickupAddr?.placeId
      );
      if (
        storeSettings?.pickup?.enabled &&
        !hasPickupAddress &&
        settings.businessLocation
      ) {
        storeSettings.pickup = {
          ...storeSettings.pickup,
          address: {
            ...(storeSettings.pickup.address || {}),
            street: settings.businessLocation,
          },
        };
      }

      setInitialValues({
        ...INITIAL_VALUES,
        ...settings,
        afterHoursPrice: settings.afterHoursPriceEnabled
          ? settings.afterHoursPrice
          : "",
        bankAccount: {
          accountNumber: settings?.bankAccount?.accountNumber || "",
        },
        _servicesImageInitialUrl: settings.servicesImageUrl ?? null,
        _storeImageInitialUrl: settings.storeImageUrl ?? null,
        _restaurantImageInitialUrl: settings.restaurantImageUrl ?? null,
        _servicesImageTempFile: null,
        _storeImageTempFile: null,
        _restaurantImageTempFile: null,
        _servicesImageDeleted: false,
        _storeImageDeleted: false,
        _restaurantImageDeleted: false,
        openingHours: settings.openingHours?.length
          ? settings.openingHours
          : EMPTY_OPENING_HOURS,
        storeSettings,
        restaurantSettings:
          settings.restaurantSettings || EMPTY_RESTAURANT_FULFILLMENT,
      });

      setBanks(banksResponse.data.banks || []);
      setZones(zonesResponse.data || []);

      const selectedDistricts = settings.districts || [];
      setSelectedAreas(selectedDistricts.map((district) => district.id));
      setSelectedLanguages(settings.languages || []);

      const bankAccount = settings.bankAccount || {};
      const bankId = bankAccount?.bankId || null;
      const branchId = bankAccount?.branchId || null;

      if (bankId) {
        setSelectedBank(Number(bankId));
        await fetchBankBranches(bankId);
      }
      if (branchId) {
        setSelectedBranch(Number(branchId));
      }
    } catch (error) {
      console.error("Error in getSettings:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useLayoutEffect(() => {
    getSettings();
  }, [getSettings]);

  const handleBankChange = async (newBankId) => {
    const bankId = Number(newBankId);
    setSelectedBank(bankId);
    setSelectedBranch(null);
    await fetchBankBranches(bankId);
  };

  const handleBranchChange = (newBranchId) => {
    setSelectedBranch(Number(newBranchId));
  };

  const handleSubmitForm = async (data, { setSubmitting, setFieldValue }) => {
    setBankErrors({ bankId: "", branchId: "", accountNumber: "" });

    const acct = data.bankAccount.accountNumber
      ? String(data.bankAccount.accountNumber).trim()
      : "";

    const anyFilled = !!acct || !!selectedBank || !!selectedBranch;
    const allFilled = !!acct && !!selectedBank && !!selectedBranch;

    if (anyFilled && !allFilled) {
      setBankErrors({
        accountNumber: acct ? "" : t("account_number_required"),
        bankId: selectedBank ? "" : t("bank_required"),
        branchId: selectedBranch ? "" : t("branch_required"),
      });
      setSubmitting(false);
      return;
    }

    try {
      setIsLoading(true);

      const imageOps = BUSINESS_IMAGE_FIELDS.map((field) => {
        const tempFile = data[field.tempField];
        const wasDeleted = data[field.deletedField];
        const initialUrl = data[field.initialField];

        if (tempFile) {
          return uploadImageOfType(tempFile, field.type).then((url) => {
            if (url) setFieldValue(field.urlField, url);
            setFieldValue(field.initialField, url);
            setFieldValue(field.tempField, null);
            setFieldValue(field.deletedField, false);
            return { ok: true };
          });
        }
        if (wasDeleted && initialUrl) {
          return deleteImageOfType(field.type).then(() => {
            setFieldValue(field.urlField, null);
            setFieldValue(field.initialField, null);
            setFieldValue(field.deletedField, false);
            return { ok: true };
          });
        }
        return Promise.resolve(null);
      });

      const profileBody = {
        contactFullName: data.contactFullName,
        contactPhonePrefix: data.contactPhonePrefix,
        contactPhoneNumber: data.contactPhoneNumber,
        contactEmail: data.contactEmail,
        description: data.description,
        languages: selectedLanguages,
        districts: selectedAreas,
        afterHoursPrice: data.afterHoursPrice,
        afterHoursPriceEnabled: data.afterHoursPriceEnabled,
      };

      const sectionRequests = [
        apiPutRequest(endpoints.updateSupplierProfile, profileBody),
        apiPutRequest(endpoints.updateSupplierOpeningHours, {
          openingHours: data.openingHours,
        }),
        apiPutRequest(endpoints.updateSupplierStoreSettings, {
          storeSettings: data.storeSettings,
        }),
      ];

      // Only persist restaurant settings while the section is visible; hidden
      // means we leave any existing restaurant data untouched on save.
      if (SHOW_RESTAURANT_SETTINGS) {
        sectionRequests.push(
          apiPutRequest(endpoints.updateSupplierRestaurantSettings, {
            restaurantSettings: data.restaurantSettings,
          })
        );
      }

      // Bank account is optional — only send if all three fields are present.
      if (allFilled) {
        sectionRequests.push(
          apiPutRequest(endpoints.updateSupplierBankAccount, {
            bankAccount: {
              accountNumber: data.bankAccount.accountNumber,
              bankId: selectedBank,
              branchId: selectedBranch,
            },
          })
        );
      }

      const results = await Promise.allSettled([...imageOps, ...sectionRequests]);

      const failures = results.filter((r) => r.status === "rejected");

      emitter.emit("fetch-get-started-content");
      emitter.emit("supplier-image-updated", null);

      if (failures.length > 0) {
        const first = failures[0];
        // axios rejects with an AxiosError whose .message is the generic
        // "Request failed with status code 400"; the actual BE payload
        // lives at error.response.data (shape: { status, message } from
        // Admin_system's errorHandler middleware). Read from there so
        // our stable error codes propagate.
        const beCode =
          first?.reason?.response?.data?.message ?? first?.reason?.message;
        // Map stable BE error codes to localized FE messages. The BE
        // throws "MUST_CHOOSE_FULFILLMENT_OPTION" when a supplier with
        // active products tries to disable both self-pickup AND all
        // delivery areas — block-and-explain rather than silently
        // saving a store nobody can buy from.
        const localized =
          beCode === "MUST_CHOOSE_FULFILLMENT_OPTION"
            ? t("must_choose_fulfillment_option_error")
            : t("settings_saved_error");
        throw new Error(localized);
      }

      setSubmitStatus({
        open: true,
        message: t("settings_saved_success"),
        severity: "success",
      });
    } catch (error) {
      setSubmitStatus({
        open: true,
        message: error.message || t("settings_saved_error"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const { saveButtonWrapperStyles, saveChangesButtonStyles } = muiStyles;
  const schema = validationSchema(t);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleSubmitForm}
      enableReinitialize
      validateOnBlur={true}
      validateOnChange={false}
      validateOnMount={false}
    >
      {({ handleSubmit, isSubmitting }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                width: "100%",
                backgroundColor: "#f8f7fa",
                marginTop: { xs: "75px", md: "0px" },
              }}
            >
              <BusinessProfile />
              <ContactDetails isloading={isloading} />
              <MakeBusinessShine
                isloading={isloading}
                setSubmitStatus={setSubmitStatus}
              />
              <OpeningHoursWorkingDays isloading={isloading} />
              <ServiceSettings
                {...{
                  isloading,
                  zones,
                  selectedAreas,
                  setSelectedAreas,
                  languages,
                  selectedLanguages,
                  setSelectedLanguages,
                }}
              />
              <StoreSettings isloading={isloading} zones={zones} />
              {SHOW_RESTAURANT_SETTINGS && (
                <RestaurantSettings isloading={isloading} zones={zones} />
              )}
              <BankAccount
                {...{
                  isloading,
                  banks,
                  branches,
                  selectedBank,
                  selectedBranch,
                  handleBankChange,
                  handleBranchChange,
                  bankErrors,
                  setBankErrors,
                }}
              />

              <Box sx={saveButtonWrapperStyles}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={saveChangesButtonStyles}
                >
                  {t("save_changes")}
                </Button>
              </Box>

              <Snackbar
                open={submitStatus.open}
                autoHideDuration={6000}
                onClose={() =>
                  setSubmitStatus((prev) => ({ ...prev, open: false }))
                }
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <Alert
                  onClose={() =>
                    setSubmitStatus((prev) => ({ ...prev, open: false }))
                  }
                  severity={submitStatus.severity}
                  sx={{ width: "100%" }}
                >
                  {submitStatus.message}
                </Alert>
              </Snackbar>
            </Box>
          </form>
        );
      }}
    </Formik>
  );
}
